import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import LoadingDataDiv from "../common/LoadingDataDiv";
import { toastError, toastSuccess } from "../../../helpers/Toast";
import "../../../css/Scheduler.css";

const typeLabels = {
  DAY_OFF: "Prośba o wolne",
  VACATION: "Urlop / nieobecność",
  AVAILABILITY: "Deklaracja dostępności",
  OPEN_SHIFT: "Otwarta zmiana",
  SHIFT_SWAP: "Zamiana zmiany"
};

const weekdayLabels = {
  MONDAY: "Pn",
  TUESDAY: "Wt",
  WEDNESDAY: "Śr",
  THURSDAY: "Cz",
  FRIDAY: "Pt",
  SATURDAY: "So",
  SUNDAY: "Nd"
};

const formatWeekdays = days => (days || []).map(day => weekdayLabels[day] || day).join(", ");

const statusLabels = {
  AWAITING_TARGET: "Czeka na drugiego pracownika",
  PENDING: "Oczekuje na kierownika",
  APPROVED: "Zatwierdzony",
  REJECTED: "Odrzucony",
  CANCELLED: "Anulowany"
};

const availabilityLabels = {
  AVAILABLE: "Mogę pracować",
  PREFERRED: "Preferuję pracę"
};

const formatAvailability = request => {
  if (request.type !== "AVAILABILITY") return null;
  const parts = [availabilityLabels[request.availabilityType] || request.availabilityType];
  if (request.availabilityTimeFrom && request.availabilityTimeTo) {
    parts.push(`${String(request.availabilityTimeFrom).slice(0, 5)}–${String(request.availabilityTimeTo).slice(0, 5)}`);
  }
  if (request.availabilityRecurrence === "WEEKLY" && request.availabilityDaysOfWeek?.length) {
    parts.push(formatWeekdays(request.availabilityDaysOfWeek));
  }
  return parts.filter(Boolean).join(" · ");
};

const formatTime = value => value ? value.slice(11, 16) : "";
const formatDate = value => value ? value.slice(0, 10) : "";
const formatDateTime = value => value ? new Date(value).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" }) : "";
const getErrorMessage = error => error?.response?.data?.message || (typeof error?.response?.data === "string" ? error.response.data : null) || "Operacja nie powiodła się.";

const requestPeriod = request => {
  if (request.dateFrom && request.dateTo) return request.dateFrom === request.dateTo ? request.dateFrom : `${request.dateFrom} – ${request.dateTo}`;
  const shift = request.targetShift || request.sourceShift;
  return shift ? `${formatDate(shift.startsAt)} ${formatTime(shift.startsAt)}–${formatTime(shift.endsAt)}` : "";
};

const ShiftSummary = ({ shift, prefix }) => {
  if (!shift) return null;
  return (
    <div className="schedule-manager-shift-summary">
      <strong>{prefix}</strong>
      <span>{formatDate(shift.startsAt)} · {formatTime(shift.startsAt)}–{formatTime(shift.endsAt)}</span>
      <span>{shift.employeeFullName || "Nieobsadzona"} · {shift.positionName || "Brak stanowiska"}</span>
    </div>
  );
};

const ScheduleChangeRequestsManager = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [decisionType, setDecisionType] = useState(null);
  const [managerComment, setManagerComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [limitCheck, setLimitCheck] = useState(null);
  const [limitCheckLoading, setLimitCheckLoading] = useState(false);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await axiosPrivate.get("/schedule-change-requests/manager", {
        params: statusFilter ? { status: statusFilter } : {}
      });
      setRequests(response.data || []);
    } catch (error) {
      setRequests([]);
      toastError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [axiosPrivate, statusFilter]);

  const departments = useMemo(() => {
    const map = new Map();
    requests.forEach(request => {
      if (request.departmentId) map.set(request.departmentId, request.departmentName || `Dział #${request.departmentId}`);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], "pl"));
  }, [requests]);

  const visibleRequests = useMemo(() => {
    const employeeQuery = employeeFilter.trim().toLocaleLowerCase("pl-PL");
    return requests.filter(request => {
      if (departmentFilter && String(request.departmentId) !== String(departmentFilter)) return false;
      if (typeFilter && request.type !== typeFilter) return false;
      if (employeeQuery && !String(request.employeeFullName || "").toLocaleLowerCase("pl-PL").includes(employeeQuery)) return false;
      return true;
    });
  }, [requests, departmentFilter, typeFilter, employeeFilter]);

  const openDecision = (request, type) => {
    setSelectedRequest(request);
    setDecisionType(type);
    setManagerComment("");
    setLimitCheck(null);
  };

  const closeDecision = () => {
    setSelectedRequest(null);
    setDecisionType(null);
    setManagerComment("");
    setLimitCheck(null);
  };

  useEffect(() => {
    const loadLimitCheck = async () => {
      if (!selectedRequest || decisionType !== "approve" || selectedRequest.type !== "VACATION") {
        setLimitCheck(null);
        return;
      }
      setLimitCheckLoading(true);
      try {
        const response = await axiosPrivate.get(`/department-vacations/requests/${selectedRequest.id}/limit-check`);
        setLimitCheck(response.data);
      } catch (error) {
        setLimitCheck(null);
        toastError(getErrorMessage(error));
      } finally {
        setLimitCheckLoading(false);
      }
    };
    loadLimitCheck();
  }, [axiosPrivate, selectedRequest, decisionType]);

  const submitDecision = async () => {
    if (!selectedRequest || !decisionType) return;
    setSaving(true);
    try {
      await axiosPrivate.post(`/schedule-change-requests/${selectedRequest.id}/${decisionType}`, {
        managerComment
      });
      toastSuccess(decisionType === "approve" ? "Wniosek został zatwierdzony." : "Wniosek został odrzucony.");
      closeDecision();
      await loadRequests();
    } catch (error) {
      toastError(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="layoutSidenav_content">
      <ToastContainer />
      <div className="app-component-card-header shadow schedule-manager-requests-header">
        <div>
          <div className="my-schedule-title">Wnioski do grafiku</div>
          <div className="my-schedule-subtitle">Akceptacja próśb o wolne, urlopów, deklaracji dostępności, otwartych zmian i zamian pracowników.</div>
        </div>
        <Button size="sm" variant="outline-primary" onClick={() => navigate("/app/department-vacations")}>Urlopy zbiorczo</Button>
      </div>

      <div className="container-fluid px-4">
        <div className="schedule-section">
          <div className="schedule-manager-request-filters">
            <Form.Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
              <option value="PENDING">Oczekujące na kierownika</option>
              <option value="AWAITING_TARGET">Czekające na drugiego pracownika</option>
              <option value="APPROVED">Zatwierdzone</option>
              <option value="REJECTED">Odrzucone</option>
              <option value="CANCELLED">Anulowane</option>
              <option value="">Wszystkie</option>
            </Form.Select>
            <Form.Select value={departmentFilter} onChange={event => setDepartmentFilter(event.target.value)}>
              <option value="">Wszystkie działy</option>
              {departments.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </Form.Select>
            <Form.Select value={typeFilter} onChange={event => setTypeFilter(event.target.value)}>
              <option value="">Wszystkie typy</option>
              {Object.entries(typeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Form.Select>
            <Form.Control
              type="search"
              value={employeeFilter}
              onChange={event => setEmployeeFilter(event.target.value)}
              placeholder="Pracownik"
              aria-label="Filtruj po pracowniku"
              style={{ minWidth: 180, maxWidth: 220 }}
            />
            <Button size="sm" variant="outline-secondary" onClick={loadRequests}>Odśwież</Button>
            <span className="schedule-manager-request-count">{visibleRequests.length} wniosków</span>
          </div>

          {loading ? <LoadingDataDiv /> : visibleRequests.length === 0 ? (
            <div className="my-schedule-empty">Brak wniosków spełniających wybrane kryteria.</div>
          ) : (
            <div className="schedule-manager-request-list">
              {visibleRequests.map(request => (
                <article key={request.id} className="schedule-manager-request-card">
                  <div className="schedule-manager-request-top">
                    <div>
                      <div className="schedule-manager-request-title">
                        <strong>{request.employeeFullName}</strong>
                        <span>{typeLabels[request.type] || request.type}</span>
                      </div>
                      <div className="schedule-request-card-meta">
                        <span>{request.departmentName}</span>
                        <span>{requestPeriod(request)}</span>
                        <span>Złożono: {formatDateTime(request.createdAt)}</span>
                      </div>
                    </div>
                    <span className={`schedule-request-status schedule-request-status-${String(request.status).toLowerCase()}`}>{statusLabels[request.status] || request.status}</span>
                  </div>

                  {request.absenceTypeName && <div className="schedule-manager-request-detail"><strong>Typ nieobecności:</strong> {request.absenceTypeName}</div>}
                  {request.type === "AVAILABILITY" && <div className="schedule-manager-request-detail"><strong>Dostępność:</strong> {formatAvailability(request)}</div>}
                  {request.sourceShift && <ShiftSummary shift={request.sourceShift} prefix="Zmiana pracownika" />}
                  {request.targetShift && <ShiftSummary shift={request.targetShift} prefix={request.type === "SHIFT_SWAP" ? "Zmiana do przejęcia" : "Otwarta zmiana"} />}
                  {request.employeeComment && <div className="schedule-request-comment"><strong>Uwagi pracownika:</strong> {request.employeeComment}</div>}
                  {request.managerComment && <div className="schedule-request-manager-comment"><strong>Decyzja:</strong> {request.managerComment}</div>}

                  {request.status === "PENDING" && (
                    <div className="schedule-manager-request-actions">
                      <Button size="sm" variant="success" onClick={() => openDecision(request, "approve")}>Zatwierdź</Button>
                      <Button size="sm" variant="outline-danger" onClick={() => openDecision(request, "reject")}>Odrzuć</Button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      <Modal show={!!selectedRequest} onHide={closeDecision} centered>
        <Modal.Header closeButton>
          <Modal.Title>{decisionType === "approve" ? "Zatwierdzenie wniosku" : "Odrzucenie wniosku"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest && (
            <div className="mb-3">
              <strong>{selectedRequest.employeeFullName}</strong><br />
              {typeLabels[selectedRequest.type]} · {requestPeriod(selectedRequest)}
            </div>
          )}
          <Form.Group>
            <Form.Label>Komentarz do decyzji</Form.Label>
            <Form.Control as="textarea" rows={3} value={managerComment} onChange={event => setManagerComment(event.target.value)} />
          </Form.Group>
          {decisionType === "approve" && selectedRequest?.type === "VACATION" && (
            <div className="schedule-manager-approval-info mt-3">Po zatwierdzeniu zostanie utworzona nieobecność. Jeżeli pracownik ma w tym okresie zaplanowane zmiany, zostaną zwolnione i staną się otwartymi zmianami do ponownego obsadzenia. Nieaktualne zgłoszenia do otwartych zmian i propozycje zamiany zostaną automatycznie odrzucone.</div>
          )}
          {decisionType === "approve" && selectedRequest?.type === "VACATION" && limitCheckLoading && (
            <div className="schedule-manager-approval-info mt-3">Sprawdzanie limitu urlopów działu...</div>
          )}
          {decisionType === "approve" && selectedRequest?.type === "VACATION" && limitCheck?.applies && (
            <div className={`schedule-vacation-limit-check mt-3 ${limitCheck.wouldExceed ? "exceeded" : limitCheck.reachesLimit ? "reached" : "ok"}`}>
              <strong>Limit urlopów działu: {limitCheck.limit}</strong>
              <div>{limitCheck.message}</div>
              <div>Maksymalnie po zatwierdzeniu: {limitCheck.maxProjectedCount}/{limitCheck.limit}. Tryb: {limitCheck.action === "BLOCK" ? "blokada" : "ostrzeżenie"}.</div>
            </div>
          )}
          {decisionType === "approve" && selectedRequest?.type === "VACATION" && limitCheck && !limitCheck.applies && (
            <div className="schedule-manager-approval-info mt-3">{limitCheck.message}</div>
          )}
          {decisionType === "approve" && selectedRequest?.type === "DAY_OFF" && (
            <div className="schedule-manager-approval-info mt-3">Po zatwierdzeniu okres zostanie zapisany jako niedostępność. Jeżeli pracownik ma w tym okresie zaplanowane zmiany, zostaną zwolnione i staną się otwartymi zmianami do ponownego obsadzenia. Nieaktualne zgłoszenia do otwartych zmian i propozycje zamiany zostaną automatycznie odrzucone.</div>
          )}
          {decisionType === "approve" && selectedRequest?.type === "AVAILABILITY" && (
            <div className="schedule-manager-approval-info mt-3">Po zatwierdzeniu deklaracja zostanie dopisana do kalendarza dostępności pracownika i od tego momentu będzie uwzględniana przez generator oraz diagnostykę grafiku. Deklaracja nie zastępuje urlopu ani prośby o wolne.</div>
          )}
          {decisionType === "approve" && ["OPEN_SHIFT", "SHIFT_SWAP"].includes(selectedRequest?.type) && (
            <div className="schedule-manager-approval-info mt-3">Przed zapisem backend ponownie sprawdzi kolizje, dostępność, umowę i limity czasu pracy.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDecision}>Anuluj</Button>
          <Button variant={decisionType === "approve" ? "success" : "danger"} onClick={submitDecision} disabled={saving || (decisionType === "approve" && selectedRequest?.type === "VACATION" && limitCheck?.wouldExceed && limitCheck?.action === "BLOCK")}>
            {saving ? "Zapisywanie..." : decisionType === "approve" ? "Zatwierdź" : "Odrzuć"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ScheduleChangeRequestsManager;

